import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import appConfig from '../config/app-config';

const API = appConfig.apis.communicationApiEnv;

interface ContactData {
    id: string;
    name: string;
    taxId: string;
    phones: Array<{ id: string; number: string; whatsapp: boolean }>;
    emails: Array<{ id: string; address: string }>;
}

interface Props {
    show: boolean;
    contactId?: string;
    debtorName: string;
    debtorDocument: string;
    receivables: any[];
}

export default function useDebtorContactModal({ show, contactId, debtorName, debtorDocument, receivables }: Props) {
    const [contact, setContact] = useState<ContactData | null>(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [showCallForm, setShowCallForm] = useState(false);

    const totals = useMemo(() => ({
        count: receivables.length,
        amount: receivables.reduce((s: number, r: any) => s + (r.amount || 0), 0),
    }), [receivables]);

    useEffect(() => {
        if (!show) return;
        setLoading(true);
        setNotFound(false);
        setContact(null);

        const doFetch = async () => {
            try {
                const { Auth } = await import('aws-amplify');
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const headers: Record<string, string> = {
                    'x-api-key': API.key || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                };

                let matchId = contactId;
                if (!matchId) {
                    const digits = debtorDocument.replace(/\D/g, '');
                    const query = digits.length >= 3 ? digits : debtorName;
                    if (query.length < 3) { setNotFound(true); setLoading(false); return; }

                    const searchRes = await fetch(`${API.address}/contacts/search?q=${encodeURIComponent(query)}`, { headers });
                    const searchData = await searchRes.json();
                    const match = digits.length >= 3
                        ? searchData.items?.find((c: any) => c.taxId?.replace(/\D/g, '') === digits)
                        : searchData.items?.[0];
                    if (!match) { setNotFound(true); setLoading(false); return; }
                    matchId = match.id;
                }

                const detailRes = await fetch(`${API.address}/contacts/${matchId}/page-view`, { headers });
                const detail = await detailRes.json();
                const phones = (detail.contactPoints || [])
                    .filter((cp: any) => (cp.type === 'PHONE' || cp.type === 'WHATSAPP') && cp.isActive)
                    .map((cp: any) => ({ id: cp.id, number: cp.valueFormatted, whatsapp: cp.isWhatsapp || cp.type === 'WHATSAPP' }));
                const emails = (detail.contactPoints || [])
                    .filter((cp: any) => cp.type === 'EMAIL' && cp.isActive)
                    .map((cp: any) => ({ id: cp.id, address: cp.valueFormatted }));
                setContact({ id: detail.id, name: detail.fullName, taxId: detail.taxIdFormatted || detail.taxId, phones, emails });
            } catch {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        doFetch();
    }, [show, contactId, debtorDocument, debtorName]);

    const handleAction = (action: 'call' | 'email' | 'validate') => {
        const now = new Date().toLocaleString('pt-BR');
        const labels = { call: 'Ligação registrada', email: 'E-mail enviado', validate: 'Validação registrada' };
        toast.success(`${labels[action]} em ${now}`);
    };

    return {
        contact, loading, notFound,
        showCallForm, setShowCallForm,
        totals, handleAction,
    };
}
